/**
 * Hook for managing menu scope side effects
 * Handles permissions setup, navigation redirects, and analytics tracking
 *
 * This hook manages organization access validation by ensuring users have both
 * the admin.store permission AND a valid organization (storeDraft) configured.
 * This prevents users with admin.store permission but no organization setup
 * from accessing organization-level features.
 *
 * @module hooks/menuScope/useMenuScopeEffects
 *
 * @param {boolean} permissionOrganizationAccess - Organization permission flag (admin.store)
 * @param {string} selectedScope - Currently selected scope
 * @param {string} currentPath - Current location pathname
 * @param {boolean} [trackEvents=false] - Whether to track analytics events
 * @param {Object|null} [storeDraft=null] - Store draft object for organization validation
 *
 * @returns {void} This hook only handles side effects
 *
 * @example
 * useMenuScopeEffects({
 *   permissionOrganizationAccess: true,
 *   selectedScope: 'STORE',
 *   currentPath: '/menu-maker',
 *   trackEvents: true,
 *   storeDraft: currentStoreDraft
 * });
 */

import { useEffect } from 'react';
import useAmplitude from 'providers/root/AmplitudeProvider/useAmplitude';
import { Store } from 'containers/SignIn/types';

import { useMenuScopeManagement } from './useMenuScopeManagement';

interface UseMenuScopeEffectsParams {
  permissionOrganizationAccess: boolean;
  selectedScope: string | null;
  currentPath: string;
  trackEvents?: boolean;
  storeDraft?: Store | null;
}

export const useMenuScopeEffects = ({
  permissionOrganizationAccess,
  trackEvents = false,
  storeDraft = null,
}: UseMenuScopeEffectsParams): void => {
  const { logEvent } = useAmplitude();
  const { setOrganizationAccess } = useMenuScopeManagement();

  // Set organization access permission based on specific organization permission AND storeDraft availability
  // Users need both admin.store permission AND a valid organization (storeDraft) to access organization scope
  useEffect(() => {
    // The user has organization access if:
    // 1. They have admin.store permission AND
    // 2. They have a valid storeDraft (organization configured)
    // This ensures users with admin.store but without organization setup cannot access organization scope
    const hasValidOrganizationAccess = permissionOrganizationAccess && storeDraft !== null;

    setOrganizationAccess(hasValidOrganizationAccess);
  }, [permissionOrganizationAccess, storeDraft, setOrganizationAccess]);

  // Note: Auto-redirect logic moved to MenuMakerRoutes.js to prevent visual jumps
  // The route-level redirect is more efficient and eliminates the brief flash of MenuScope component

  // Track scope selection view event (only if trackEvents is enabled)
  useEffect(() => {
    if (trackEvents) {
      logEvent('@event.$menuMaker.viewScopeSelection');
    }
  }, [logEvent, trackEvents]);
};
